import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { exportJobApi } from '../features/exports/job-api';
import { backgroundJobTask, sortTasks, taskCounts, type UiTask } from './task-model';
import { TASKS_CHANGED_EVENT } from './task-events';

interface LocalTaskInput extends Omit<UiTask, 'source'> {
  source?: 'local';
}

interface TaskContextValue {
  remoteJobsEnabled: boolean;
  tasks: UiTask[];
  loading: boolean;
  error: string;
  counts: ReturnType<typeof taskCounts>;
  refresh: () => Promise<void>;
  retry: (task: UiTask) => Promise<void>;
  upsertLocalTask: (task: LocalTaskInput) => void;
  removeLocalTask: (taskId: string) => void;
}

const TaskContext = createContext<TaskContextValue | null>(null);

export function TaskProvider({ remoteJobsEnabled, workspaceKey, children }: {
  remoteJobsEnabled: boolean;
  workspaceKey: string;
  children: ReactNode;
}) {
  const [remoteTasks, setRemoteTasks] = useState<UiTask[]>([]);
  const [localTasks, setLocalTasks] = useState<UiTask[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const refreshingRef = useRef(false);

  const refresh = useCallback(async () => {
    if (!remoteJobsEnabled || refreshingRef.current) return;
    refreshingRef.current = true;
    setLoading(true);
    try {
      const result = await exportJobApi.list(60);
      setRemoteTasks(result.jobs.map(backgroundJobTask));
      setError('');
    } catch (cause) {
      console.warn('Task system refresh failed', cause);
      setError(cause instanceof Error ? cause.message : 'Task system refresh failed');
    } finally {
      refreshingRef.current = false;
      setLoading(false);
    }
  }, [remoteJobsEnabled]);

  useEffect(() => {
    setRemoteTasks([]);
    setLocalTasks([]);
    setError('');
    if (remoteJobsEnabled) void refresh();
  }, [refresh, remoteJobsEnabled, workspaceKey]);

  const activeRemote = remoteTasks.some(task => task.status === 'queued' || task.status === 'running');
  useEffect(() => {
    if (!remoteJobsEnabled) return;
    const interval = activeRemote ? 2400 : 18000;
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') void refresh();
    }, interval);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void refresh();
    };
    const onChanged = () => void refresh();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener(TASKS_CHANGED_EVENT, onChanged);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener(TASKS_CHANGED_EVENT, onChanged);
    };
  }, [activeRemote, refresh, remoteJobsEnabled]);

  const upsertLocalTask = useCallback((input: LocalTaskInput) => {
    const task: UiTask = { ...input, source: 'local' };
    setLocalTasks(current => [task, ...current.filter(item => item.id !== task.id)]);
  }, []);
  const removeLocalTask = useCallback((taskId: string) => {
    setLocalTasks(current => current.filter(item => item.id !== taskId));
  }, []);

  const retry = useCallback(async (task: UiTask) => {
    if (task.source !== 'background' || !task.remoteJob || !task.retryable) return;
    await exportJobApi.retry(task.remoteJob.id);
    await refresh();
  }, [refresh]);

  const tasks = useMemo(() => sortTasks([...localTasks, ...remoteTasks]), [localTasks, remoteTasks]);
  const counts = useMemo(() => taskCounts(tasks), [tasks]);
  const value = useMemo<TaskContextValue>(() => ({
    remoteJobsEnabled, tasks, loading, error, counts, refresh, retry, upsertLocalTask, removeLocalTask
  }), [counts, error, loading, refresh, remoteJobsEnabled, removeLocalTask, retry, tasks, upsertLocalTask]);

  return <TaskContext.Provider value={value}>{children}</TaskContext.Provider>;
}

export function useTaskSystem(): TaskContextValue {
  const context = useContext(TaskContext);
  if (!context) throw new Error('TaskProvider is missing');
  return context;
}
